import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuthStore } from "../../stores/auth.store";
import { useFiltersStore } from "../../stores/filters.store";
import signOutIcon from "../../assets/images/sign-out-icon.svg";
import userAvatar from "../../assets/images/user-avatar.svg";
import nagwaLogo from "../../assets/images/nagwa-logo.svg";
import "./Header.scss";

interface NavTab {
  id: string;
  label: string;
  icon: string;
  path: string;
  permission?: (p: ReturnType<typeof usePermissions>) => boolean;
}

const Header: React.FC = () => {
  const { getResourceValue } = useResources();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const permissions = usePermissions();
  const { resetFilters } = useFiltersStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navTabs: NavTab[] = [
    {
      id: "jobs",
      label: getResourceValue("jobs", "Jobs"),
      icon: "briefcase-icon",
      path: "/",
    },
    {
      id: "users",
      label: getResourceValue("users", "Users"),
      icon: "users-icon",
      path: "/users",
      permission: (p) => p.canViewUsers,
    },
    {
      id: "sections",
      label: getResourceValue("sections", "Sections"),
      icon: "layer-group-icon",
      path: "/sections",
      permission: (p) => p.canViewSections,
    },
    {
      id: "updated-questions",
      label: getResourceValue("updated_questions", "Updated Questions"),
      icon: "arrows-rotate-icon",
      path: "/questions/update",
    },
    {
      id: "preview-questions",
      label: getResourceValue("preview_questions", "Preview Questions"),
      icon: "eye-icon",
      path: "/questions/preview",
    },
    {
      id: "reports",
      label: getResourceValue("reports", "Reports"),
      icon: "file-chart-icon",
      path: "/reports",
    },
  ];

  // Filter tabs based on permissions
  const visibleTabs = navTabs.filter(
    (tab) => !tab.permission || tab.permission(permissions)
  );

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    
    // Check for exact matches first
    const exactMatch = visibleTabs.find((tab) => tab.path === path);
    if (exactMatch) return exactMatch.id;
    
    // Check for path starts with (for nested routes)
    if (path.startsWith("/jobs/") || path === "/" || path === "/upload-jobs") return "jobs";
    if (path.startsWith("/users")) return "users";
    if (path.startsWith("/sections")) return "sections";
    if (path.startsWith("/questions/update") || path === "/update-jobs") return "updated-questions";
    if (path.startsWith("/questions/preview") || path.startsWith("/preview-questions")) return "preview-questions";
    if (path.startsWith("/reports")) return "reports";
    
    return "jobs";
  };

  const activeTab = getActiveTab();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTabClick = (path: string) => {
    const currentPath = location.pathname;
    
    // Check if navigating between jobs and updated-questions tabs
    const isFromJobsTab = currentPath === "/" || currentPath.startsWith("/jobs/") || currentPath === "/upload-jobs";
    const isFromUpdateTab = currentPath === "/questions/update" || currentPath === "/update-jobs";
    const isToJobsTab = path === "/";
    const isToUpdateTab = path === "/questions/update";
    
    // Reset filters when switching between jobs and updated-questions tabs
    if ((isFromJobsTab && isToUpdateTab) || (isFromUpdateTab && isToJobsTab)) {
      resetFilters();
    }
    
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const userName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.email?.split("@")[0] || "User";

  return (
    <header className="top-bar">
      {/* Header Section */}
      <div className="top-bar__header">
        <div className="top-bar__logo" onClick={() => navigate("/")}>
          <img
            src={nagwaLogo}
            alt="Nagwa Logo"
            className="top-bar__logo-image"
          />
          <span className="top-bar__logo-text">{getResourceValue('app_name',"Nagwa QMS")}</span>
        </div>

        <div className="top-bar__user-info" ref={dropdownRef}>
          <button
            className="top-bar__user-button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <img src={userAvatar} alt="" className="top-bar__avatar" />
            <span className="top-bar__user-name">{userName}</span>
            <i className={`f-icon chevron-down-icon ${isDropdownOpen ? "rotated" : ""}`}></i>
          </button>

          {isDropdownOpen && (
            <div className="top-bar__dropdown">
              <button className="top-bar__dropdown-item" onClick={handleLogout}>
                <img src={signOutIcon} alt="" className="top-bar__dropdown-icon" />
                <span>{getResourceValue("sign_out", "Sign Out")}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className="top-bar__nav">
        <ul className="top-bar__nav-list">
          {visibleTabs.map((tab) => (
            <li key={tab.id} className="top-bar__nav-item">
              <button
                className={`top-bar__nav-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabClick(tab.path)}
              >
                <i className={`f-icon ${tab.icon}`}></i>
                <span>{tab.label}</span>
                <div className="top-bar__nav-indicator"></div>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};

export default Header;

